import React, { useState, useEffect } from "react";
import "./Attendance.css";
import { attendanceService, userService, projectService } from "../services/api";

const Attendance = ({ user }) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [location, setLocation] = useState("office");
  const [shift, setShift] = useState("work");
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState("calendar"); // calendar, list, statistics
  const [, setServerDate] = useState(null);

  // Load server time on component mount
  useEffect(() => {
    const loadServerTime = async () => {
      try {
        const token = localStorage.getItem("token");
        // Use relative URL - nginx proxy will forward to backend
        const apiUrl = "/api/server-time";

        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        console.log("🕐 Server time:", data);

        if (data.date && data.year && data.month) {
          setServerDate(data.date);
          setSelectedDate(data.date);
          setCurrentMonth(data.month - 1); // JavaScript months are 0-indexed
          setCurrentYear(data.year);
        } else {
          // Fallback to client time if server response is invalid
          const now = new Date();
          setCurrentMonth(now.getMonth());
          setCurrentYear(now.getFullYear());
        }
      } catch (error) {
        console.error("Error loading server time:", error);
        // Fallback to client time
        const now = new Date();
        setCurrentMonth(now.getMonth());
        setCurrentYear(now.getFullYear());
      }
    };
    loadServerTime();
  }, []);
  const [filterUser, setFilterUser] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterShift, setFilterShift] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [statistics, setStatistics] = useState({
    totalDays: 0,
    totalHours: 0,
    byLocation: {},
    byShift: {},
    byUser: {},
  });
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  // Admin rozšírené stavy
  const [showAdminEdit, setShowAdminEdit] = useState(false);
  const [adminEditData, setAdminEditData] = useState({
    start_time: "",
    end_time: "",
    break_duration: 30,
    project_id: "",
    notes: "",
  });
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [absenceData, setAbsenceData] = useState({
    reason: "sick",
    notes: "",
  });
  const [projects, setProjects] = useState([]);

  // Safety check for user
  useEffect(() => {
    if (user) {
      loadAttendanceData();
      if (user.role === "admin") {
        loadUsers();
        loadProjects();
      } else {
        // For non-admin users, use their own ID
        setSelectedUserId(user.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, user, selectedUserId]);

  // Rotation data is now loaded automatically with attendance data from project_rotations table

  useEffect(() => {
    calculateStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceData, filterUser, filterLocation, filterShift, filterProject, filterStatus, filterDateFrom, filterDateTo, selectedUserId]);

  // Synchronize selectedUserId with filterUser for filtering
  useEffect(() => {
    if (selectedUserId === null) {
      setFilterUser("all");
    } else {
      setFilterUser(selectedUserId.toString());
    }
  }, [selectedUserId]);

  if (!user) {
    return (
      <div className="attendance-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam používateľské údaje...</p>
        </div>
      </div>
    );
  }

  const loadAttendanceData = async () => {
    try {
      setLoading(true);
      // Ensure we have valid month and year before making API call
      if (currentMonth === undefined || currentYear === undefined) {
        console.warn("⚠️ Month or year not yet initialized, skipping load");
        return;
      }
      const month = (currentMonth + 1).toString().padStart(2, "0");
      const year = currentYear.toString();

      let data;
      if (user.role === "admin") {
        if (selectedUserId) {
          // Load data for specific user
          data = await attendanceService.getByUser(selectedUserId, {
            month,
            year,
          });
        } else {
          // Load all data
          data = await attendanceService.getAll({ month, year });
        }
      } else {
        data = await attendanceService.getMy({ month, year });
      }

      setAttendanceData(data);
    } catch (error) {
      console.error("Error loading attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await userService.getAll();
      // Exclude blocked users from the dropdown (robust across types: 0/1, true/false, '0'/'1')
      const isBlocked = (val) => val === 1 || val === true || val === "1";
      const active = Array.isArray(data)
        ? data.filter((u) => !isBlocked(u.blocked))
        : [];

      // If the currently selected user became blocked or missing, reset selection
      if (selectedUserId && !active.some((u) => u.id === selectedUserId)) {
        setSelectedUserId(null);
      }

      setUsers(
        active.map((u) => ({
          id: u.id,
          name:
            u.full_name ||
            `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
            u.username ||
            `User ${u.id}`,
          role: u.role,
          project_id: u.project_id, // CRITICAL: Include project_id in user mapping
        })),
      );
      console.log(
        "🔍 DEBUG: Loaded users (active only) with project_id:",
        active.map((u) => ({
          id: u.id,
          name: u.full_name || u.username,
          project_id: u.project_id,
        })),
      );
    } catch (error) {
      console.error("Error loading users:", error);
      // Fallback to empty array if API fails
      setUsers([]);
    }
  };

  // loadRotationData is no longer needed - attendance data now includes rotation info from project_rotations table

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const getAttendanceStatus = (att) => {
    if (att.is_absent || ["sick", "vacation", "personal", "holiday"].includes(att.shift)) {
      return "absent";
    }

    if (att.shift === "home" || att.location === "home") {
      return "free";
    }

    return "work";
  };

  const getUserName = (att) =>
    att.user?.full_name ||
    users.find((u) => Number(u.id) === Number(att.user_id))?.name ||
    att.full_name ||
    `User ${att.user_id}`;

  const getProjectName = (att) =>
    att.project_name ||
    projects.find((project) => Number(project.id) === Number(att.project_id))?.name ||
    (att.project_id ? `Projekt ${att.project_id}` : "Bez projektu");

  const getFilteredAttendanceData = () => attendanceData.filter((att) => {
    if (filterUser !== "all" && Number(att.user_id) !== parseInt(filterUser)) return false;
    if (filterLocation !== "all" && att.location !== filterLocation) return false;
    if (filterShift !== "all" && att.shift !== filterShift) return false;
    if (filterProject !== "all" && Number(att.project_id) !== parseInt(filterProject)) return false;
    if (filterStatus !== "all" && getAttendanceStatus(att) !== filterStatus) return false;
    if (filterDateFrom && att.date < filterDateFrom) return false;
    if (filterDateTo && att.date > filterDateTo) return false;
    return true;
  });

  const calculateStatistics = () => {
    const filteredData = getFilteredAttendanceData();

    const stats = {
      totalDays: filteredData.length,
      totalHours: filteredData.reduce((sum, att) => sum + Number(att.hours_worked || att.hours || 8), 0),
      byLocation: {},
      byShift: {},
      byUser: {},
    };

    filteredData.forEach((att) => {
      stats.byLocation[att.location] =
        (stats.byLocation[att.location] || 0) + 1;
      stats.byShift[att.shift] = (stats.byShift[att.shift] || 0) + 1;
      stats.byUser[getUserName(att)] = (stats.byUser[getUserName(att)] || 0) + 1;
    });

    setStatistics(stats);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Určiť shift hodnotu pre databázu
      const shiftValue = location === "home" ? "home" : "work";

      // Kontrola: Ak klikneme na "Doma" a záznam už existuje (je "home"), zmažeme záznam
      const existingRecord = attendanceData.find(
        (att) => att.date === selectedDate && att.location === "home",
      );

      if (location === "home" && existingRecord) {
        // Zmazať existujúci záznam "home"
        await attendanceService.delete(existingRecord.id);
        await loadAttendanceData();
        setShowForm(false);
        alert('Záznam "Doma" bol odstránený');
        return;
      }

      // Admin používa rotation endpoint pre správneho používateľa
      if (user.role === "admin" && selectedUserId) {
        const targetUser = users.find((u) => u.id === parseInt(selectedUserId));
        const projectId = targetUser?.project_id || 1;

        console.log("📝 Admin submitting attendance:", {
          selectedUserId,
          projectId,
          selectedDate,
          shiftValue,
          location,
          shift,
        });

        const response = await fetch(
          `/api/rotations/user/${selectedUserId}/project/${projectId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              date: selectedDate,
              status: shiftValue,
            }),
          },
        );

        if (!response.ok) {
          const error = await response.text();
          console.error("❌ Failed to create attendance:", error);
          throw new Error("Failed to create attendance record");
        }

        const result = await response.json();
        console.log("✅ Attendance created:", result);
      } else {
        // Non-admin používa rotation endpoint pre seba
        const projectId = user.project_id || 1;

        // Determine status from location and shift
        let status;
        if (location === "home") {
          status = "home";
        } else if (location === "office") {
          status = shift; // 'morning', 'afternoon', 'night', or 'work'
        } else {
          status = "work";
        }

        console.log("📝 Employee submitting rotation:", {
          userId: user.id,
          projectId,
          date: selectedDate,
          status,
          location,
          shift,
        });

        const response = await fetch(
          `/api/rotations/user/${user.id}/project/${projectId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              date: selectedDate,
              status: status,
            }),
          },
        );

        if (!response.ok) {
          const error = await response.text();
          console.error("❌ Failed to create rotation:", error);
          throw new Error("Failed to create rotation record");
        }

        const result = await response.json();
        console.log("✅ Employee rotation created:", result);
      }

      await loadAttendanceData(); // Reload attendance data
      setShowForm(false);
      alert("Dochádzka úspešne zaznamenaná!");
    } catch (error) {
      console.error("Error submitting attendance:", error);
      alert("Nastala chyba pri zaznamenaní dochádzky");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Kontrola: Ak zmeníme na "Doma" a aktuálny záznam už je "home", zmažeme záznam
      if (
        location === "home" &&
        selectedAttendance &&
        selectedAttendance.location === "home"
      ) {
        // Zmazať existujúci záznam "home"
        await attendanceService.delete(selectedAttendance.id);
        loadAttendanceData();
        setShowEditForm(false);
        setSelectedAttendance(null);
        alert('Záznam "Doma" bol odstránený');
        return;
      }

      // Pre "home" location, shift má byť null (žiadna zmena)
      const payload = {
        date: selectedDate,
        location,
        shift: location === "home" ? null : shift,
      };

      await attendanceService.update(selectedAttendance.id, payload);

      loadAttendanceData();
      setShowEditForm(false);
      setSelectedAttendance(null);
      alert("Dochádzka úspešne upravená!");
    } catch (error) {
      console.error("Error updating attendance:", error);
      alert("Nastala chyba pri úprave dochádzky");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attendanceId) => {
    if (window.confirm("Naozaj chcete vymazať tento záznam dochádzky?")) {
      try {
        setLoading(true);
        await attendanceService.delete(attendanceId);
        loadAttendanceData();
        alert("Dochádzka úspešne vymazaná!");
      } catch (error) {
        console.error("Error deleting attendance:", error);
        alert("Nastala chyba pri mazaní dochádzky");
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusName = (status) => {
    switch (status) {
      case "work": return "Práca";
      case "free": return "Voľno";
      case "absent": return "Neprítomnosť";
      default: return status;
    }
  };

  const getExportRows = () => getFilteredAttendanceData()
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((att) => ({
      date: att.date,
      userName: getUserName(att),
      projectName: getProjectName(att),
      locationName: getLocationName(att.location),
      shiftName: getShiftName(att.shift),
      statusName: getStatusName(getAttendanceStatus(att)),
      hours: Number(att.hours_worked || att.hours || 8)
    }));

  const downloadBlob = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

  const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");


  const exportToCSV = () => {
    const headers = ["Dátum", "Používateľ", "Projekt", "Miesto", "Zmena", "Stav", "Hodiny"];
    const rows = getExportRows().map((row) => [
      row.date,
      row.userName,
      row.projectName,
      row.locationName,
      row.shiftName,
      row.statusName,
      row.hours
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    downloadBlob(`\ufeff${csv}`, "text/csv;charset=utf-8;", `attendance_${currentYear}_${currentMonth + 1}.csv`);
  };

  const exportToExcel = () => {
    const rows = getExportRows();
    const htmlRows = rows.map((row) => `
      <tr>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.userName)}</td>
        <td>${escapeHtml(row.projectName)}</td>
        <td>${escapeHtml(row.locationName)}</td>
        <td>${escapeHtml(row.shiftName)}</td>
        <td>${escapeHtml(row.statusName)}</td>
        <td>${escapeHtml(row.hours)}</td>
      </tr>
    `).join("");
    const html = `
      <html>
        <head><meta charset="utf-8" /></head>
        <body>
          <table border="1">
            <thead>
              <tr><th>Dátum</th><th>Používateľ</th><th>Projekt</th><th>Miesto</th><th>Zmena</th><th>Stav</th><th>Hodiny</th></tr>
            </thead>
            <tbody>${htmlRows}</tbody>
          </table>
        </body>
      </html>
    `;

    downloadBlob(`\ufeff${html}`, "application/vnd.ms-excel;charset=utf-8;", `attendance_${currentYear}_${currentMonth + 1}.xls`);
  };

  const printFilteredReport = () => {
    const rows = getExportRows();
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Pre PDF report povoľte v prehliadači otváranie okien.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Dochádzka report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            p { color: #666; margin-top: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; text-align: left; }
            th { background: #f4f6f8; }
          </style>
        </head>
        <body>
          <h1>Dochádzka report</h1>
          <p>Vyfiltrované záznamy: ${rows.length}</p>
          <table>
            <thead>
              <tr><th>Dátum</th><th>Používateľ</th><th>Projekt</th><th>Miesto</th><th>Zmena</th><th>Stav</th><th>Hodiny</th></tr>
            </thead>
            <tbody>
              ${rows.map((row) => `<tr><td>${escapeHtml(row.date)}</td><td>${escapeHtml(row.userName)}</td><td>${escapeHtml(row.projectName)}</td><td>${escapeHtml(row.locationName)}</td><td>${escapeHtml(row.shiftName)}</td><td>${escapeHtml(row.statusName)}</td><td>${escapeHtml(row.hours)}</td></tr>`).join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const getLocationName = (location) => {
    switch (location) {
      case "office":
        return "Práca";
      case "home":
        return "Domov";
      default:
        return location;
    }
  };

  const getShiftName = (shift) => {
    switch (shift) {
      case "morning":
        return "Ranná";
      case "afternoon":
        return "Poobedná";
      case "night":
        return "Nočná";
      default:
        return shift;
    }
  };

  const handleAdminEdit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await attendanceService.adminUpdate(selectedAttendance.id, adminEditData);

      alert("Dochádzka bola úspešne upravená");
      setShowAdminEdit(false);
      setSelectedAttendance(null);
      setAdminEditData({
        start_time: "",
        end_time: "",
        break_duration: 30,
        project_id: "",
        notes: "",
      });
      loadAttendanceData();
    } catch (error) {
      console.error("Error updating attendance:", error);
      alert("Nastala chyba pri úprave dochádzky");
    } finally {
      setLoading(false);
    }
  };

  const handleAbsence = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const absenceEntry = {
        user_id: selectedUserId || user.id,
        date: selectedDate,
        reason: absenceData.reason,
        notes: absenceData.notes,
        is_absence: true,
      };

      await attendanceService.createAbsence(absenceEntry);

      alert("Neprítomnosť bola úspešne zaznamenaná");
      setShowAbsenceForm(false);
      setAbsenceData({
        reason: "sick",
        notes: "",
      });
      loadAttendanceData();
    } catch (error) {
      console.error("Error recording absence:", error);
      alert("Nastala chyba pri zaznamenávaní neprítomnosti");
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getRotationForDay = (day) => {
    // Find attendance record for this day
    const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    const record = attendanceData.find((att) => {
      // Filter by selected user if admin has selected a specific user
      if (user.role === "admin" && selectedUserId) {
        return att.date === dateStr && att.user_id === selectedUserId;
      }
      // For non-admin users, show their own data
      return att.date === dateStr;
    });

    if (!record) {
      return null;
    }

    // Map shift to color gradient and label
    // Práca: zelená farba
    if (record.shift === "work") {
      return {
        type: "work",
        label: "💼 Práca",
        colorFrom: "#28a745", // green
        colorTo: "#28a745", // green
      };
    }
    // Ranná (5:00-14:00): béžová farba
    else if (record.shift === "morning") {
      return {
        type: "morning",
        label: "🌅 Ranná zmena (5:00-14:00)",
        colorFrom: "#D2B48C", // tan/béžová
        colorTo: "#D2B48C", // tan/béžová
      };
    }
    // Poobedná/Denná (13:00-22:00): modrá farba
    else if (record.shift === "afternoon") {
      return {
        type: "afternoon",
        label: "☀️ Denná zmena (13:00-22:00)",
        colorFrom: "#00BFFF", // deep sky blue
        colorTo: "#00BFFF", // deep sky blue
      };
    }
    // Nočná (21:00-6:00): fialová farba
    else if (record.shift === "night") {
      return {
        type: "night",
        label: "🌙 Nočná zmena (21:00-6:00)",
        colorFrom: "#9400D3", // dark violet
        colorTo: "#9400D3", // dark violet
      };
    }
    // Legacy support for old rotation types
    else if (record.shift === "home") {
      return {
        type: "home",
        label: "Domov",
        colorFrom: "#dc3545",
        colorTo: "#dc3545",
      };
    } else if (record.shift === "sick") {
      return {
        type: "sick",
        label: "Choroba",
        colorFrom: "#6c757d",
        colorTo: "#6c757d",
      };
    }

    console.log(`❌ Day ${day} has no recognized shift type: ${record.shift}`);
    return null;
  };

  const getAttendanceForDate = (date) => {
    const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, "0")}-${date.toString().padStart(2, "0")}`;

    // Filter by selected user if admin has selected a specific user
    let result;
    if (user.role === "admin" && selectedUserId) {
      result = attendanceData.find(
        (att) => att.date === dateStr && att.user_id === selectedUserId,
      );
    } else {
      // For non-admin users or when no specific user is selected, show all
      result = attendanceData.find((att) => att.date === dateStr);
    }

    return result;
  };

  const getLocationColor = (location) => {
    switch (location) {
      case "office":
        return "#28a745"; // green
      case "home":
        return "#007bff"; // blue
      default:
        return "#6c757d"; // gray
    }
  };

  const getShiftIcon = (shift) => {
    switch (shift) {
      case "morning":
        return "🌅";
      case "afternoon":
        return "☀️";
      case "night":
        return "🌙";
      default:
        return "🕐";
    }
  };

  // Výpočet ISO týždňa (pondelok ako prvý deň)
  const getWeekNumber = (date) => {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const dayNum = d.getUTCDay() || 7; // Nedeľa = 7, Pondelok = 1
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  // Získanie pondelka daného týždňa
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Pondelok
    return new Date(d.setDate(diff));
  };

  const monthNames = [
    "Január",
    "Február",
    "Marec",
    "Apríl",
    "Máj",
    "Jún",
    "Júl",
    "August",
    "September",
    "Október",
    "November",
    "December",
  ];

  const dayNamesShort = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"]; // Pondelok-Nedeľa
  const dayNamesLong = [
    "pondelok",
    "utorok",
    "streda",
    "štvrtok",
    "piatok",
    "sobota",
    "nedeľa",
  ];

  const formatLocalDate = (date) => (
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  );

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  // Convert Sunday=0 to Sunday=6 for Monday-based week
  // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
  // We need: 0=Monday, 1=Tuesday, ..., 6=Sunday
  const firstDayMonday = firstDay === 0 ? 6 : firstDay - 1;
  const days = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayMonday; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const attendance = getAttendanceForDate(day);
    const rotation = getRotationForDay(day);
    const isToday =
      day === new Date().getDate() &&
      currentMonth === new Date().getMonth() &&
      currentYear === new Date().getFullYear();

    // Determine background style
    let dayStyle = {};
    let dayNumberStyle = {};
    let tooltipText = "";

    if (rotation) {
      // Has shift assignment - show solid color
      dayStyle = {
        backgroundColor: rotation.colorFrom,
        borderRadius: "8px",
      };
      dayNumberStyle = {
        color: "#ffffff",
        textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
        fontWeight: "bold",
      };
      tooltipText = rotation.label;
    } else if (attendance) {
      // Has attendance but no rotation - show background based on shift/location
      let bgColor = "#28a745"; // default green
      let icon = "💼";
      let label = "Práca";

      // Determine color by location or shift
      if (attendance.location === "office" || attendance.location === "other") {
        bgColor = "#28a745"; // green for work
        icon = "💼";
        label = "Práca";
      } else if (attendance.location === "home") {
        bgColor = "#dc3545"; // red for home
        icon = "🏠";
        label = "Doma";
      }

      // Override with shift-specific colors if shift is set
      if (attendance.shift === "morning") {
        bgColor = "#28a745"; // green - morning shift
        icon = "🌅";
        label = "Ranná zmena";
      } else if (attendance.shift === "afternoon") {
        bgColor = "#ffc107"; // yellow/orange - afternoon shift
        icon = "☀️";
        label = "Poobedná zmena";
      } else if (attendance.shift === "night") {
        bgColor = "#17a2b8"; // blue - night shift
        icon = "🌙";
        label = "Nočná zmena";
      }

      dayStyle = { backgroundColor: bgColor, borderRadius: "8px" };
      dayNumberStyle = { color: "#ffffff", fontWeight: "bold" };
      tooltipText = `${icon} ${label}`;
    } else {
      // No attendance - white/empty
      dayStyle = {
        backgroundColor: "#ffffff",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      };
      dayNumberStyle = { color: "#495057" };
      tooltipText = "Žiadna dochádzka";
    }

    days.push(
      <div
        key={day}
        className={`calendar-day ${isToday ? "today" : ""} ${rotation ? "has-rotation" : ""} ${attendance ? "has-attendance" : ""}`}
        style={dayStyle}
        onClick={() => {
          const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
          setSelectedDate(dateStr);
          setShowForm(true);
        }}
        title={tooltipText}
      >
        <div className="day-number" style={dayNumberStyle}>
          {day}
        </div>
        {rotation && (
          <div style={{ fontSize: "1.5em", marginTop: "5px" }}>
            {getShiftIcon(rotation.type)}
          </div>
        )}
      </div>,
    );
  }

  // Handler pre kliknutie na celý týždeň
  const handleWeekClick = async (weekMonday) => {
    // Len admin môže označovať celý týždeň
    if (user.role !== "admin") {
      alert("Nemáte oprávnenie označovať celý týždeň!");
      return;
    }

    // Admin musí vybrať používateľa
    if (!selectedUserId && user.role === "admin") {
      alert("Vyberte používateľa!");
      return;
    }

    // Určiť cieľového používateľa
    const targetUserId =
      user.role === "admin" ? parseInt(selectedUserId) : user.id;

    console.log(
      "📅 KW clicked - weekMonday input:",
      weekMonday,
      weekMonday.toISOString(),
    );

    // Získať všetkých 7 dní týždňa (Pondelok až Nedeľa)
    // FIX: Use local date format instead of ISO to avoid timezone offset issues
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekMonday);
      d.setDate(d.getDate() + i);
      // Use local date format: YYYY-MM-DD
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      weekDates.push(dateStr);
    }

    console.log("📅 KW clicked - marking week:", weekDates);

    // Získať meno používateľa pre zobrazenie
    const targetUser = users.find((u) => u.id === targetUserId);
    const userName = targetUser?.name || "Neznámy";

    // Zobraziť dialóg pre výber typu dochádzky
    const choice = window.prompt(
      `Označiť celý týždeň pre: ${userName}\n` +
        `Dátumy: ${weekDates[0]} - ${weekDates[6]}\n\n` +
        `1 = Práca (office - zelená)\n` +
        `2 = Doma (home - červená)\n` +
        `3 = Ranná zmena (morning - zelená/žltá)\n` +
        `4 = Denná zmena (afternoon - zelená/modrá)\n` +
        `5 = Nočná zmena (night - zelená/červená)\n` +
        `6 = Zrušiť\n\n` +
        `Zadajte číslo (1-6):`,
    );

    if (!choice || choice === "6") {
      return; // Zrušené
    }

    let location, shift;

    switch (choice) {
      case "1":
        location = "office";
        shift = "work";
        break;
      case "2":
        location = "home";
        shift = "home";
        break;
      case "3":
        location = "office";
        shift = "morning";
        break;
      case "4":
        location = "office";
        shift = "afternoon";
        break;
      case "5":
        location = "office";
        shift = "night";
        break;
      default:
        alert("Neplatná voľba!");
        return;
    }

    try {
      setLoading(true);

      // Získať project_id pre používateľa (už máme targetUser z predchádzajúceho kroku)
      const projectId = targetUser?.project_id || 1; // Default to project 1

      console.log("📅 Marking week:", {
        weekDates,
        shift,
        location,
        userId: targetUserId,
        projectId,
        userName,
      });

      // Označiť všetkých 7 dní pomocou admin endpoint (prepíše existujúce záznamy)
      for (const dateStr of weekDates) {
        try {
          // Použiť admin rotation endpoint - automaticky prepíše existujúce záznamy
          const response = await fetch(
            `/api/rotations/user/${targetUserId}/project/${projectId}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify({
                date: dateStr,
                status: shift,
              }),
            },
          );

          if (!response.ok) {
            console.error(`Failed to mark day ${dateStr}:`, response.status);
          } else {
            const result = await response.json();
            console.log(`✅ Day ${dateStr} marked as ${shift}:`, result);
          }
        } catch (dayError) {
          console.error(`Error marking day ${dateStr}:`, dayError);
        }
      }

      // Reload data after marking all days
      await loadAttendanceData();

      // Zobraziť správu s názvom zmeny
      let shiftName;
      switch (shift) {
        case "work":
          shiftName = "Práca";
          break;
        case "home":
          shiftName = "Doma";
          break;
        case "morning":
          shiftName = "Ranná zmena";
          break;
        case "afternoon":
          shiftName = "Denná zmena";
          break;
        case "night":
          shiftName = "Nočná zmena";
          break;
        default:
          shiftName = shift;
      }

      alert(
        `Celý týždeň (${weekDates[0]} - ${weekDates[6]}) pre ${userName} bol označený ako "${shiftName}"`,
      );
    } catch (error) {
      console.error("Error marking week:", error);
      alert("Nastala chyba pri označovaní týždňa");
    } finally {
      setLoading(false);
    }
  };

  // Handler pre kliknutie na deň v tabuľke
  const handleDayTableClick = (dateStr) => {
    setSelectedDate(dateStr);
    setShowForm(true);
  };

  // Handler pre kliknutie na hlavičku dňa v mesiaci (napr. všetky pondelky)
  const handleWeekdayHeaderClick = async (weekdayIndex) => {
    if (user.role !== "admin") {
      alert("Nemáte oprávnenie hromadne označovať dni!");
      return;
    }

    if (!selectedUserId) {
      alert("Vyberte používateľa!");
      return;
    }

    const targetUserId = parseInt(selectedUserId);
    const targetUser = users.find((u) => Number(u.id) === targetUserId);
    const userName = targetUser?.name || "Neznámy";
    const dates = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const mondayBasedDay = date.getDay() === 0 ? 6 : date.getDay() - 1;
      if (mondayBasedDay === weekdayIndex) {
        dates.push(formatLocalDate(date));
      }
    }

    if (dates.length === 0) return;

    const dayName = dayNamesLong[weekdayIndex] || dayNamesShort[weekdayIndex];
    const choice = window.prompt(
      `Označiť všetky dni "${dayName}" v mesiaci ${monthNames[currentMonth]} ${currentYear} pre: ${userName}\n` +
        `Počet dní: ${dates.length}\n` +
        `Dátumy: ${dates.join(", ")}\n\n` +
        `1 = Práca (zelená)\n` +
        `2 = Doma (červená)\n` +
        `3 = Zrušiť\n\n` +
        `Zadajte číslo (1-3):`,
    );

    if (!choice || choice === "3") return;

    let status;
    let statusLabel;
    switch (choice) {
      case "1":
        status = "work";
        statusLabel = "Práca";
        break;
      case "2":
        status = "home";
        statusLabel = "Doma";
        break;
      default:
        alert("Neplatná voľba!");
        return;
    }

    try {
      setLoading(true);
      const projectId = targetUser?.project_id || 1;
      let failedCount = 0;

      for (const dateStr of dates) {
        try {
          const response = await fetch(
            `/api/rotations/user/${targetUserId}/project/${projectId}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify({
                date: dateStr,
                status,
              }),
            },
          );

          if (!response.ok) {
            failedCount += 1;
            console.error(`Failed to mark weekday ${dateStr}:`, response.status);
          }
        } catch (dayError) {
          failedCount += 1;
          console.error(`Error marking weekday ${dateStr}:`, dayError);
        }
      }

      await loadAttendanceData();

      if (failedCount > 0) {
        alert(`Označenie dokončené s chybami. Nepodarilo sa označiť ${failedCount} z ${dates.length} dní.`);
      } else {
        alert(`Všetky dni "${dayName}" v mesiaci ${monthNames[currentMonth]} pre ${userName} boli označené ako "${statusLabel}".`);
      }
    } catch (error) {
      console.error("Error marking weekday in month:", error);
      alert("Nastala chyba pri hromadnom označovaní dní");
    } finally {
      setLoading(false);
    }
  };


  // Tabuľkový mesačný kalendár (ako ProjectDetail)
  const renderMonthlyCalendar = () => {
    const today = new Date();
    // Získanie prvého a posledného dňa mesiaca
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    // Získanie prvého pondelka v zobrazení
    const firstMonday = getMondayOfWeek(firstDayOfMonth);

    // Získanie poslednej nedele v zobrazení (koniec týždňa obsahujúceho posledný deň mesiaca)
    const lastSunday = new Date(lastDayOfMonth);
    const dayOfWeek = lastSunday.getDay();
    if (dayOfWeek !== 0) {
      // Ak nie je nedeľa
      lastSunday.setDate(lastSunday.getDate() + (7 - dayOfWeek));
    }

    // Vytvorenie týždňov
    const weeks = [];
    let currentDate = new Date(firstMonday);

    // Iteruj týždne, kým nepokryjeme celý mesiac + jeho okrajové týždne
    while (currentDate <= lastSunday) {
      const weekStart = new Date(currentDate);
      const weekNumber = getWeekNumber(weekStart);
      const weekDays = [];

      for (let i = 0; i < 7; i++) {
        const day = new Date(currentDate);
        // FIX: Use local date instead of UTC to avoid timezone shift
        const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
        const isCurrentMonth = day.getMonth() === currentMonth;

        // Získať attendance pre tento deň
        let attendance = null;

        if (selectedUserId) {
          attendance = attendanceData.find(
            (att) => att.date === dateStr && att.user_id === selectedUserId,
          );
        } else {
          attendance = attendanceData.find((att) => att.date === dateStr);
        }

        // Určenie farby (bez ikony)
        let cellStyle = {};
        let tooltipText = "";

        if (attendance) {
          if (attendance.location === "home") {
            // DOMA: iba červená
            cellStyle = { backgroundColor: "#dc3545" };
            tooltipText = "Doma";
          } else if (attendance.location === "office") {
            // PRÁCA: podľa shift_type (position) alebo default zelená
            const shiftType = attendance.shift_type || attendance.position;
            if (shiftType === "morning") {
              cellStyle = { backgroundColor: "#D2B48C" };
              tooltipText = "Ranná zmena";
            } else if (shiftType === "afternoon") {
              cellStyle = { backgroundColor: "#00BFFF" };
              tooltipText = "Denná zmena";
            } else if (shiftType === "night") {
              cellStyle = { backgroundColor: "#9400D3" };
              tooltipText = "Nočná zmena";
            } else {
              // Práca bez špeciálnej zmeny
              cellStyle = { backgroundColor: "#28a745" };
              tooltipText = "Práca";
            }
          }
        } else {
          // Žiadna dochádzka
          cellStyle = { backgroundColor: "#f8f9fa" };
          tooltipText = "Žiadna dochádzka";
        }

        const isToday = day.toDateString() === today.toDateString();

        weekDays.push({
          date: day,
          dateStr,
          isCurrentMonth,
          isToday,
          attendance,
          cellStyle,
          tooltipText,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      weeks.push({
        weekNumber,
        weekStart,
        days: weekDays,
      });
    }

    return (
      <div className="attendance-monthly-calendar">
        <div className="calendar-table">
          {/* Header */}
          <div className="calendar-table-header">
            {user.role === "admin" && <div className="kw-header">KW</div>}
            {dayNamesShort.map((dayName, idx) => (
              <div
                key={idx}
                className={`day-header ${user.role === "admin" ? "clickable" : ""}`}
                onClick={user.role === "admin" ? () => handleWeekdayHeaderClick(idx) : undefined}
                title={user.role === "admin" ? `Kliknutím označíte všetky dni ${dayNamesLong[idx]} v aktuálnom mesiaci` : undefined}
              >
                {dayName}
              </div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, weekIdx) => (
            <div key={weekIdx} className="calendar-table-row">
              {/* KW Column - len pre adminov */}
              {user.role === "admin" && (
                <div
                  className="kw-cell clickable"
                  onClick={() => handleWeekClick(week.weekStart)}
                  title={`Kliknutím označíte celý týždeň ${week.weekNumber}`}
                >
                  {week.weekNumber}
                </div>
              )}

              {/* Days */}
              {week.days.map((dayInfo, dayIdx) => {
                const isAdmin = user.role === "admin";

                return (
                  <div
                    key={dayIdx}
                    className={`calendar-table-cell ${dayInfo.isCurrentMonth ? "" : "other-month"} ${dayInfo.isToday ? "today-cell" : ""} ${isAdmin ? "clickable" : ""}`}
                    style={dayInfo.cellStyle}
                    onClick={
                      isAdmin
                        ? () => handleDayTableClick(dayInfo.dateStr)
                        : undefined
                    }
                    title={
                      dayInfo.tooltipText +
                      (isAdmin ? " (kliknutím označíte)" : "")
                    }
                  >
                    <span className="day-num">{dayInfo.date.getDate()}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="attendance-container">
      <div className="attendance-header">
        <h1 className="attendance-title">📅 Dochádzka a Kalendár</h1>
        <div className="header-controls">
          <div className="view-mode-tabs">
            <button
              className={`tab-btn ${viewMode === "calendar" ? "active" : ""}`}
              onClick={() => setViewMode("calendar")}
            >
              📅 Kalendár
            </button>
            <button
              className={`tab-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
            >
              📋 Zoznam
            </button>
            <button
              className={`tab-btn ${viewMode === "statistics" ? "active" : ""}`}
              onClick={() => setViewMode("statistics")}
            >
              📊 Štatistiky
            </button>
          </div>
          <div className="action-buttons-wrapper">
            <div className="action-buttons-row">
              {user.role === "admin" && (
                <button
                  className="absence-btn"
                  onClick={() => setShowAbsenceForm(true)}
                >
                  ❌ Označiť neprítomnosť
                </button>
              )}
              <button
                className="add-attendance-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("🔘 Button clicked - opening form");
                  setShowForm(true);
                }}
              >
                ➕ Označiť dochádzku
              </button>
            </div>
            <div className="export-buttons-row">
              <button className="export-btn" onClick={exportToCSV}>
                📥 CSV
              </button>
              <button className="export-btn excel" onClick={exportToExcel}>
                📊 Excel
              </button>
              <button className="export-btn pdf" onClick={printFilteredReport}>
                🖨️ PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {user.role === "admin" && (
        <div className="filters-section">
          <div className="filters">
            <div className="filter-group">
              <label>Používateľ:</label>
              <select
                value={selectedUserId || "all"}
                onChange={(e) => {
                  const userId =
                    e.target.value === "all" ? null : parseInt(e.target.value);
                  setSelectedUserId(userId);
                }}
              >
                <option value="all">Všetci používatelia</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Projekt:</label>
              <select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              >
                <option value="all">Všetky projekty</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Miesto:</label>
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
              >
                <option value="all">Všetky miesta</option>
                <option value="office">Práca</option>
                <option value="home">Domov</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Zmena:</label>
              <select
                value={filterShift}
                onChange={(e) => setFilterShift(e.target.value)}
              >
                <option value="all">Všetky zmeny</option>
                <option value="work">Práca</option>
                <option value="home">Domov</option>
                <option value="morning">Ranná</option>
                <option value="afternoon">Poobedná</option>
                <option value="night">Nočná</option>
                <option value="sick">PN</option>
                <option value="vacation">Dovolenka</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Stav:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Všetky stavy</option>
                <option value="work">Práca</option>
                <option value="free">Voľno</option>
                <option value="absent">Neprítomnosť</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Od dátumu:</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Do dátumu:</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
              />
            </div>
            <div className="filter-group filter-actions">
              <button
                type="button"
                className="clear-filters-btn"
                onClick={() => {
                  setSelectedUserId(null);
                  setFilterProject("all");
                  setFilterLocation("all");
                  setFilterShift("all");
                  setFilterStatus("all");
                  setFilterDateFrom("");
                  setFilterDateTo("");
                }}
              >
                Vyčistiť filtre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <>
          <div className="calendar-controls">
            <button
              className="nav-btn"
              onClick={() => {
                if (currentMonth === 0) {
                  setCurrentMonth(11);
                  setCurrentYear(currentYear - 1);
                } else {
                  setCurrentMonth(currentMonth - 1);
                }
              }}
            >
              ◀ Predchádzajúci
            </button>

            <h2 className="current-month">
              {monthNames[currentMonth]} {currentYear}
            </h2>

            <button
              className="nav-btn"
              onClick={() => {
                if (currentMonth === 11) {
                  setCurrentMonth(0);
                  setCurrentYear(currentYear + 1);
                } else {
                  setCurrentMonth(currentMonth + 1);
                }
              }}
            >
              Nasledujúci ▶
            </button>
          </div>

          {/* Wrapper: Kalendár + Info Panel */}
          <div className="attendance-calendar-wrapper">
            {/* Tabuľkový kalendár */}
            {loading ? (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <p>Načítavam dochádzku...</p>
              </div>
            ) : (
              renderMonthlyCalendar()
            )}

            {/* Info panel na pravej strane */}
            <div className="attendance-info-panel">
              <h3
                style={{ marginTop: 0, marginBottom: "1rem", color: "#495057" }}
              >
                📊 Štatistiky
              </h3>
              <div className="info-section">
                <p
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "14px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Mesiac:</strong> {monthNames[currentMonth]}{" "}
                  {currentYear}
                </p>
                <p
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "14px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Používateľ:</strong>{" "}
                  {!selectedUserId
                    ? "Všetci"
                    : user.role !== "admin"
                      ? user.full_name || user.username
                      : users.find((u) => u.id === parseInt(selectedUserId))
                          ?.name || "Neznámy"}
                </p>
              </div>

              <hr
                style={{
                  margin: "1rem 0",
                  border: "none",
                  borderTop: "1px solid #dee2e6",
                }}
              />

              <h4 style={{ marginBottom: "0.75rem", color: "#495057" }}>
                Počet dní:
              </h4>
              <div className="stats-counts">
                <p
                  style={{
                    margin: "0.25rem 0",
                    fontSize: "13px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Ranná zmena:</strong>{" "}
                  {
                    attendanceData.filter(
                      (a) => (a.shift_type || a.position) === "morning",
                    ).length
                  }{" "}
                  dní
                </p>
                <p
                  style={{
                    margin: "0.25rem 0",
                    fontSize: "13px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Denná zmena:</strong>{" "}
                  {
                    attendanceData.filter(
                      (a) => (a.shift_type || a.position) === "afternoon",
                    ).length
                  }{" "}
                  dní
                </p>
                <p
                  style={{
                    margin: "0.25rem 0",
                    fontSize: "13px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Nočná zmena:</strong>{" "}
                  {
                    attendanceData.filter(
                      (a) => (a.shift_type || a.position) === "night",
                    ).length
                  }{" "}
                  dní
                </p>
                <p
                  style={{
                    margin: "0.25rem 0",
                    fontSize: "13px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Práca:</strong>{" "}
                  {
                    attendanceData.filter(
                      (a) => a.shift === "work" || a.location === "office",
                    ).length
                  }{" "}
                  dní
                </p>
                <p
                  style={{
                    margin: "0.25rem 0",
                    fontSize: "13px",
                    color: "#6c757d",
                  }}
                >
                  <strong>Doma:</strong>{" "}
                  {
                    attendanceData.filter(
                      (a) => a.shift === "home" || a.location === "home",
                    ).length
                  }{" "}
                  dní
                </p>
              </div>

              <hr
                style={{
                  margin: "1rem 0",
                  border: "none",
                  borderTop: "1px solid #dee2e6",
                }}
              />

              <h4 style={{ marginBottom: "0.75rem", color: "#495057" }}>
                Legenda:
              </h4>
              <div className="legend-items">
                <div className="legend-item" style={{ marginBottom: "0.5rem" }}>
                  <div
                    className="legend-color-box"
                    style={{
                      backgroundColor: "#D2B48C",
                      width: "30px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  ></div>
                  <span style={{ fontSize: "13px" }}>Ranná zmena</span>
                </div>
                <div className="legend-item" style={{ marginBottom: "0.5rem" }}>
                  <div
                    className="legend-color-box"
                    style={{
                      backgroundColor: "#00BFFF",
                      width: "30px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  ></div>
                  <span style={{ fontSize: "13px" }}>Denná zmena</span>
                </div>
                <div className="legend-item" style={{ marginBottom: "0.5rem" }}>
                  <div
                    className="legend-color-box"
                    style={{
                      backgroundColor: "#9400D3",
                      width: "30px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  ></div>
                  <span style={{ fontSize: "13px" }}>Nočná zmena</span>
                </div>
                <div className="legend-item" style={{ marginBottom: "0.5rem" }}>
                  <div
                    className="legend-color-box"
                    style={{
                      backgroundColor: "#28a745",
                      width: "30px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  ></div>
                  <span style={{ fontSize: "13px" }}>Práca</span>
                </div>
                <div className="legend-item" style={{ marginBottom: "0.5rem" }}>
                  <div
                    className="legend-color-box"
                    style={{
                      backgroundColor: "#dc3545",
                      width: "30px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  ></div>
                  <span style={{ fontSize: "13px" }}>Doma</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="list-view">
          <div className="list-header">
            <h3>
              Zoznam dochádzky - {monthNames[currentMonth]} {currentYear}
            </h3>
            <div className="list-stats">
              <span>Celkom záznamov: {statistics.totalDays}</span>
              <span>Celkom hodín: {statistics.totalHours}</span>
            </div>
          </div>

          <div className="attendance-table">
            <table>
              <thead>
                <tr>
                  <th>Dátum</th>
                  {user.role === "admin" && <th>Používateľ</th>}
                  <th>Projekt</th>
                  <th>Miesto</th>
                  <th>Zmena</th>
                  <th>Stav</th>
                  <th>Hodiny</th>
                  {user.role === "admin" && <th>Akcie</th>}
                </tr>
              </thead>
              <tbody>
                {getFilteredAttendanceData()
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((att) => (
                    <tr key={att.id}>
                      <td>{new Date(att.date).toLocaleDateString("sk-SK")}</td>
                      {user.role === "admin" && <td>{getUserName(att)}</td>}
                      <td>{getProjectName(att)}</td>
                      <td>
                        <span
                          className="location-badge"
                          style={{
                            backgroundColor: getLocationColor(att.location),
                          }}
                        >
                          {getLocationName(att.location)}
                        </span>
                      </td>
                      <td>
                        <span className="shift-badge">
                          {getShiftIcon(att.shift)} {getShiftName(att.shift)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${getAttendanceStatus(att)}`}>
                          {getStatusName(getAttendanceStatus(att))}
                        </span>
                      </td>
                      <td>{Number(att.hours_worked || att.hours || 8)}h</td>
                      {user.role === "admin" && (
                        <td className="actions">
                          <button
                            className="edit-btn"
                            onClick={() => {
                              setSelectedAttendance(att);
                              setSelectedDate(att.date);
                              setLocation(att.location);
                              setShift(att.shift);
                              setShowEditForm(true);
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            className="admin-edit-btn"
                            onClick={() => {
                              setSelectedAttendance(att);
                              setAdminEditData({
                                start_time: att.start_time || "",
                                end_time: att.end_time || "",
                                break_duration: att.break_duration || 30,
                                project_id: att.project_id || "",
                                notes: att.notes || "",
                              });
                              setShowAdminEdit(true);
                            }}
                          >
                            ⚙️
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(att.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Statistics View */}
      {viewMode === "statistics" && (
        <div className="statistics-view">
          <div className="stats-header">
            <h3>
              Štatistiky dochádzky - {monthNames[currentMonth]} {currentYear}
            </h3>
          </div>

          <div className="attendance-statistics stats-grid">
            <div className="stat-card">
              <h4>📊 Celkové štatistiky</h4>
              <div className="stat-items">
                <div className="stat-item">
                  <span className="stat-label">Celkom dní:</span>
                  <span className="stat-value">{statistics.totalDays}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Celkom hodín:</span>
                  <span className="stat-value">{statistics.totalHours}h</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Priemerné hodiny/deň:</span>
                  <span className="stat-value">
                    {statistics.totalDays > 0
                      ? (statistics.totalHours / statistics.totalDays).toFixed(
                          1,
                        )
                      : 0}
                    h
                  </span>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <h4>📍 Podľa miesta</h4>
              <div className="stat-items">
                {Object.entries(statistics.byLocation).map(
                  ([location, count]) => (
                    <div key={location} className="stat-item">
                      <span className="stat-label">
                        <div
                          className="location-dot"
                          style={{
                            backgroundColor: getLocationColor(location),
                          }}
                        ></div>
                        {getLocationName(location)}:
                      </span>
                      <span className="stat-value">{count} dní</span>
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className="stat-card">
              <h4>🕐 Podľa zmeny</h4>
              <div className="stat-items">
                {Object.entries(statistics.byShift).map(([shift, count]) => (
                  <div key={shift} className="stat-item">
                    <span className="stat-label">
                      {getShiftIcon(shift)} {getShiftName(shift)}:
                    </span>
                    <span className="stat-value">{count} dní</span>
                  </div>
                ))}
              </div>
            </div>

            {user.role === "admin" && (
              <div className="stat-card">
                <h4>👥 Podľa používateľa</h4>
                <div className="stat-items">
                  {Object.entries(statistics.byUser).map(
                    ([userName, count]) => (
                      <div key={userName} className="stat-item">
                        <span className="stat-label">{userName}:</span>
                        <span className="stat-value">{count} dní</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            // Close modal if clicking outside
            if (e.target.className === "modal-overlay") {
              console.log("🔘 Closing modal - clicked outside");
              setShowForm(false);
            }
          }}
        >
          <div className="modal">
            <h2>📍 Označiť dochádzku</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Dátum:</label>
                <input
                  type="date"
                  value={selectedDate}
                  max={
                    user.role === "admin"
                      ? undefined
                      : new Date().toISOString().split("T")[0]
                  }
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Miesto práce:</label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                >
                  <option value="office">🏢 Práca</option>
                  <option value="home">🏠 Domov</option>
                </select>
              </div>

              {/* Zmena sa zobrazí LEN pri office, NIE pri home */}
              {location === "office" && (
                <div className="form-group">
                  <label>Zmena:</label>
                  <select
                    value={shift}
                    onChange={(e) => setShift(e.target.value)}
                    required
                  >
                    <option value="work">💼 Práca</option>
                    <option value="morning">🌅 Ranná (6:00 - 14:00)</option>
                    <option value="afternoon">
                      ☀️ Poobedná (14:00 - 22:00)
                    </option>
                    <option value="night">🌙 Nočná (22:00 - 6:00)</option>
                  </select>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? "Ukladám..." : "✅ Označiť dochádzku"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowForm(false)}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedAttendance && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>✏️ Upraviť dochádzku</h2>
            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label>Dátum:</label>
                <input
                  type="date"
                  value={selectedDate}
                  max={
                    user.role === "admin"
                      ? undefined
                      : new Date().toISOString().split("T")[0]
                  }
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Miesto práce:</label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                >
                  <option value="office">🏢 Práca</option>
                  <option value="home">🏠 Domov</option>
                </select>
              </div>

              {/* Zmena sa zobrazí LEN pri office, NIE pri home */}
              {location === "office" && (
                <div className="form-group">
                  <label>Zmena:</label>
                  <select
                    value={shift}
                    onChange={(e) => setShift(e.target.value)}
                    required
                  >
                    <option value="work">💼 Práca</option>
                    <option value="morning">🌅 Ranná (6:00 - 14:00)</option>
                    <option value="afternoon">
                      ☀️ Poobedná (14:00 - 22:00)
                    </option>
                    <option value="night">🌙 Nočná (22:00 - 6:00)</option>
                  </select>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? "Ukladám..." : "✅ Uložiť zmeny"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowEditForm(false);
                    setSelectedAttendance(null);
                  }}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Edit Form */}
      {showAdminEdit && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚙️ Admin úprava dochádzky</h3>
            <form onSubmit={handleAdminEdit}>
              <div className="form-group">
                <label>Čas príchodu:</label>
                <input
                  type="time"
                  value={adminEditData.start_time}
                  onChange={(e) =>
                    setAdminEditData({
                      ...adminEditData,
                      start_time: e.target.value,
                    })
                  }
                />
              </div>

              <div className="form-group">
                <label>Čas odchodu:</label>
                <input
                  type="time"
                  value={adminEditData.end_time}
                  onChange={(e) =>
                    setAdminEditData({
                      ...adminEditData,
                      end_time: e.target.value,
                    })
                  }
                />
              </div>

              <div className="form-group">
                <label>Prestávka (minúty):</label>
                <input
                  type="number"
                  value={adminEditData.break_duration}
                  onChange={(e) =>
                    setAdminEditData({
                      ...adminEditData,
                      break_duration: parseInt(e.target.value),
                    })
                  }
                  min="0"
                  max="120"
                />
              </div>

              <div className="form-group">
                <label>Projekt:</label>
                <select
                  value={adminEditData.project_id}
                  onChange={(e) =>
                    setAdminEditData({
                      ...adminEditData,
                      project_id: e.target.value,
                    })
                  }
                >
                  <option value="">Vyberte projekt</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Poznámky:</label>
                <textarea
                  value={adminEditData.notes}
                  onChange={(e) =>
                    setAdminEditData({
                      ...adminEditData,
                      notes: e.target.value,
                    })
                  }
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? "Ukladám..." : "✅ Uložiť"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowAdminEdit(false);
                    setSelectedAttendance(null);
                  }}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Absence Form */}
      {showAbsenceForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>❌ Označiť neprítomnosť</h3>
            <form onSubmit={handleAbsence}>
              <div className="form-group">
                <label>Dátum:</label>
                <input
                  type="date"
                  value={selectedDate}
                  max={
                    user.role === "admin"
                      ? undefined
                      : new Date().toISOString().split("T")[0]
                  }
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Dôvod neprítomnosti:</label>
                <select
                  value={absenceData.reason}
                  onChange={(e) =>
                    setAbsenceData({ ...absenceData, reason: e.target.value })
                  }
                  required
                >
                  <option value="sick">Choroba</option>
                  <option value="vacation">Dovolenka</option>
                  <option value="personal">Osobné voľno</option>
                  <option value="training">Školenie</option>
                  <option value="other">Iné</option>
                </select>
              </div>

              <div className="form-group">
                <label>Poznámky:</label>
                <textarea
                  value={absenceData.notes}
                  onChange={(e) =>
                    setAbsenceData({ ...absenceData, notes: e.target.value })
                  }
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? "Ukladám..." : "✅ Označiť neprítomnosť"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowAbsenceForm(false);
                    setAbsenceData({
                      reason: "sick",
                      notes: "",
                    });
                  }}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
